import {useState, useEffect, useMemo} from 'react'
import PropTypes from 'prop-types'
import CloseIcon from '@mui/icons-material/Close'
import {
  StyledDialog,
  StyledDialogTitle,
  StyledTitleText,
  StyledCloseButton,
  StyledDialogContent,
  StyledHeaderRow,
  StyledToggleContainer,
  StyledToggleLabel,
  StyledProjectInfoRow,
  StyledProjectInfoItem,
  StyledInfoLabel,
  StyledInfoValue,
  StyledSectionTitle,
  StyledSubSectionTitle,
  StyledChipContainer,
  StyledChip,
  StyledFlagChip,
  StyledCategoryRow,
  StyledCategoryTitle,
  StyledDivider,
  StyledPermissionSection,
  StyledNoDataText,
  StyledSwitch,
  StyledDateInfo,
  StyledDateItem,
  StyledDateLabel,
  StyledDateValue,
  StyledInsertionChip,
  StyledDeletionChip
} from './ViewPermissionModalStyled'
import {useProjectDetails} from '../../../../store/hooks/projectDetails'
import {useConfig} from '../../../../store/hooks/config'
import {useAdminPermission} from '../../../../store/hooks/adminPermission'
import {
  getJurisdictionNames,
  getDataAggregationNames,
  getFiltersByCategory,
  getFilterCategoryByFilterKey,
  getUseCasesByCategory,
  getUseCaseCategoryById,
  getPointOfInterestNames,
  getFlagValue,
  getDisplayName,
  getOriginalIds
} from './permissionMappings'
import {
  FILTER_CATEGORY_ORDER,
  USE_CASE_CATEGORY_ORDER,
  FLAG_PERMISSION_SECTIONS,
  PERMISSION_ID_MAP,
  PERMISSION_MODAL_TYPES
} from '../constants/permissionConstants'

const ViewPermissionModal = ({open, onClose, permissionData, title = PERMISSION_MODAL_TYPES.PROJECT}) => {
  const [showChanges, setShowChanges] = useState(false)

  useEffect(() => {
    if (open) {
      setShowChanges(false)
    }
  }, [open])

  const {pointOfInterestsData, useCases} = useProjectDetails()
  const {dataAggregationConfig, filterConfig} = useConfig() || {}
  const {jurisdiction} = useAdminPermission()
  const providerData = useMemo(
    () => ({
      jurisdiction,
      dataAggregationConfig,
      filterConfig,
      pointOfInterestsData,
      useCases
    }),
    [jurisdiction, dataAggregationConfig, filterConfig, pointOfInterestsData, useCases]
  )

  const isUserPermission = title === PERMISSION_MODAL_TYPES.USER
  const isCreateEvent = permissionData?.event === 'Created'

  const changedData = permissionData?.changedData || []

  const snapshotData = permissionData?.snapshot || null

  const getPermissionsFromSnapshot = () => {
    if (!snapshotData) return null
    return {
      GROUP_ID: permissionData?.projectId || '',
      GROUP_NAME: permissionData?.projectName || '',
      CLIENT_ID: permissionData?.clientId || '',
      JURISDICTION: snapshotData.JURISDICTION || [],
      FILTERS: snapshotData.FILTERS || [],
      DATA_AGGREGATION: snapshotData.DATA_AGGREGATION || [],
      TREND_ANALYSIS_FLAG: snapshotData.TREND_ANALYSIS_FLAG || [],
      EXPORT_FLAG: snapshotData.EXPORT_FLAG || [],
      CHATBOT_FLAG: snapshotData.CHATBOT_FLAG || [],
      BENEFITS: snapshotData.BENEFITS || [],
      POINT_OF_INTEREST: snapshotData.POINT_OF_INTEREST || [],
      USECASE: snapshotData.USECASE || [],
      PERSONA_FLAG: snapshotData.PERSONA_FLAG || [],
      CHATBOT_EXPORT_FLAG: snapshotData.CHATBOT_EXPORT_FLAG || [],
      CHATBOT_COPY_FLAG: snapshotData.CHATBOT_COPY_FLAG || []
    }
  }

  const permissions = getPermissionsFromSnapshot()

  const getChangesMap = () => {
    const changesMap = {}
    if (!changedData || changedData.length === 0 || !permissions) return changesMap

    for (const change of changedData) {
      if (typeof change !== 'object' || change === null) continue

      const permissionId =
        typeof change.permission_id === 'string' ? Number.parseInt(change.permission_id, 10) : change.permission_id
      const fieldName = PERMISSION_ID_MAP[permissionId]
      if (!fieldName) continue

      const addedValues = change.changed_values?.added || []
      const removedValues = change.changed_values?.removed || []

      if (!changesMap[fieldName]) {
        changesMap[fieldName] = {insertions: [], deletions: [], changeType: change.change_type}
      }

      for (const value of addedValues) {
        changesMap[fieldName].insertions.push(value)
      }

      for (const value of removedValues) {
        changesMap[fieldName].deletions.push(value)
      }
    }

    return changesMap
  }

  const changesMap = getChangesMap()

  const hasMatchingValue = (array, value) => {
    if (!array || array.length === 0) return false
    const normalizedValue = typeof value === 'number' ? value : String(value)
    return array.some((item) => {
      const normalizedItem = typeof item === 'number' ? item : String(item)
      return normalizedItem === normalizedValue || String(normalizedItem) === String(normalizedValue)
    })
  }

  const isInsertion = (fieldName, value) => {
    return hasMatchingValue(changesMap[fieldName]?.insertions, value)
  }

  const isDeletionValue = (fieldName, value) => {
    return hasMatchingValue(changesMap[fieldName]?.deletions, value)
  }

  const hasFlagChanged = (fieldName) => {
    return changesMap[fieldName]?.insertions?.length > 0 || changesMap[fieldName]?.deletions?.length > 0
  }

  const getFlagChange = (fieldName) => {
    const changes = changesMap[fieldName]
    if (!changes) return null
    const oldValue = changes.deletions.length > 0 ? changes.deletions[0] : null
    const newValue = changes.insertions.length > 0 ? changes.insertions[0] : null
    if (oldValue === null && newValue === null) return null
    return {oldValue, newValue}
  }

  const handleToggleChange = (event) => {
    setShowChanges(event.target.checked)
  }

  const getDeletedCategory = (fieldName, deletedValue) => {
    if (fieldName === 'FILTERS') {
      return getFilterCategoryByFilterKey(deletedValue, filterConfig)
    }
    if (fieldName === 'USECASE') {
      return getUseCaseCategoryById(deletedValue, useCases)
    }
    return null
  }

  const categoriesMatch = (cat1, cat2) => {
    if (cat1 === cat2) return true
    if (!cat1 || !cat2) return false
    return String(cat1).trim().toLowerCase() === String(cat2).trim().toLowerCase()
  }

  const renderChips = (items, fieldName = null, categoryName = null, isLastCategory = false) => {
    const isDeletion = (item) => {
      if (!showChanges || !fieldName || !changesMap[fieldName]?.deletions) return false
      const originalIds = getOriginalIds(fieldName, item, providerData)
      return originalIds.some((id) => isDeletionValue(fieldName, id))
    }

    const hasDeletionsToShow = () => {
      if (!showChanges || !fieldName || !changesMap[fieldName]?.deletions) return false
      const deletions = changesMap[fieldName].deletions
      if (!deletions || deletions.length === 0) return false

      if (categoryName && (fieldName === 'FILTERS' || fieldName === 'USECASE')) {
        return deletions.some((deletedValue) => {
          const deletedCategory = getDeletedCategory(fieldName, deletedValue)
          return categoriesMatch(deletedCategory, categoryName) || (deletedCategory === null && isLastCategory)
        })
      }
      return true
    }

    if ((!items || items.length === 0) && !hasDeletionsToShow()) {
      return <StyledNoDataText>No data available</StyledNoDataText>
    }

    return (
      <StyledChipContainer>
        {(items || []).map((item) => {
          if (showChanges && fieldName && changesMap[fieldName]) {
            const originalIds = getOriginalIds(fieldName, item, providerData)
            const itemIsInsertion = originalIds.some((id) => isInsertion(fieldName, id))

            if (itemIsInsertion) {
              return <StyledInsertionChip key={`ins-${item}`} label={`+ ${item}`} size='small' />
            }

            if (isDeletion(item)) {
              return null
            }
          }
          return <StyledChip key={`chip-${item}`} label={item} size='small' />
        })}
        {showChanges &&
          fieldName &&
          changesMap[fieldName]?.deletions
            ?.filter((deletedValue) => {
              if (categoryName && (fieldName === 'FILTERS' || fieldName === 'USECASE')) {
                const deletedCategory = getDeletedCategory(fieldName, deletedValue)
                return categoriesMatch(deletedCategory, categoryName) || (deletedCategory === null && isLastCategory)
              }
              return true
            })
            ?.map((deletedValue) => {
              const displayName = getDisplayName(fieldName, deletedValue, providerData)
              return <StyledDeletionChip key={`del-${deletedValue}`} label={`- ${displayName}`} size='small' />
            })}
      </StyledChipContainer>
    )
  }

  const getChangedFlagChip = (flagChange) => {
    const newLabel = flagChange.newValue === 1 ? 'Yes' : 'No'
    if (isCreateEvent && flagChange.newValue !== 1) {
      return <StyledFlagChip label={newLabel} size='small' />
    }
    if (flagChange.newValue === 1) {
      return <StyledInsertionChip label={newLabel} size='small' />
    }
    return <StyledDeletionChip label={newLabel} size='small' />
  }

  const renderFlagChip = (label, isEnabled, fieldName = null) => {
    if (showChanges && fieldName && hasFlagChanged(fieldName)) {
      const flagChange = getFlagChange(fieldName)
      if (flagChange) return getChangedFlagChip(flagChange)
    }
    return <StyledFlagChip label={isEnabled ? 'Yes' : 'No'} size='small' />
  }

  const renderCategorizedItems = (categorizedData, categoryOrder, fieldName, TitleComponent, emptyMessage) => {
    const allDeletions = (showChanges && changesMap[fieldName]?.deletions) || []

    const deletionCategories = allDeletions
      .map((deletedValue) => getDeletedCategory(fieldName, deletedValue))
      .filter((cat) => cat !== null)

    const categoryHasDeletions = (categoryName) => {
      if (!showChanges || allDeletions.length === 0) return false
      return allDeletions.some((deletedValue) => {
        const deletedCategory = getDeletedCategory(fieldName, deletedValue)
        return categoriesMatch(deletedCategory, categoryName)
      })
    }

    const allDataCategories = Object.keys(categorizedData)
    const additionalCategories = allDataCategories.filter((cat) => !categoryOrder.includes(cat))
    const deletionOnlyCategories = [
      ...new Set(
        deletionCategories.filter(
          (cat) =>
            !categoryOrder.some((co) => categoriesMatch(co, cat)) &&
            !allDataCategories.some((dc) => categoriesMatch(dc, cat))
        )
      )
    ]
    const allCategories = [...categoryOrder, ...additionalCategories, ...deletionOnlyCategories]

    const categoriesWithContent = allCategories.filter((categoryName) => {
      const items = categorizedData[categoryName]
      return (items && items.length > 0) || categoryHasDeletions(categoryName)
    })

    const hasAnyContent = categoriesWithContent.length > 0

    if (!hasAnyContent) {
      return <StyledNoDataText>{emptyMessage}</StyledNoDataText>
    }

    const result = []

    for (const categoryName of allCategories) {
      const items = categorizedData[categoryName]
      const hasDeletions = categoryHasDeletions(categoryName)

      if ((!items || items.length === 0) && !hasDeletions) continue

      result.push(
        <StyledCategoryRow key={categoryName}>
          <TitleComponent>{categoryName}</TitleComponent>
          {renderChips(items, fieldName, categoryName, false)}
        </StyledCategoryRow>
      )
    }

    return result
  }

  const renderContent = () => {
    if (!permissions) {
      return <StyledNoDataText>No permission data available</StyledNoDataText>
    }

    // Same pattern as ProjectDetailsComponent - use provider data to resolve IDs to names
    const jurisdictions = getJurisdictionNames(permissions.JURISDICTION, jurisdiction)
    const dataAggregations = getDataAggregationNames(permissions.DATA_AGGREGATION, dataAggregationConfig)
    const pointOfInterests = getPointOfInterestNames(permissions.POINT_OF_INTEREST, pointOfInterestsData)

    return (
      <>
        <StyledHeaderRow>
          <StyledToggleContainer>
            <StyledToggleLabel>View Permission Changes</StyledToggleLabel>
            <StyledSwitch size='small' checked={showChanges} onChange={handleToggleChange} />
          </StyledToggleContainer>
          <StyledDateInfo>
            <StyledDateItem>
              <StyledDateLabel>Last Permission Changed:</StyledDateLabel>
              <StyledDateValue>
                {permissionData?.timestamp ? permissionData.timestamp.split(' ')[0] : '-'}
              </StyledDateValue>
            </StyledDateItem>
          </StyledDateInfo>
        </StyledHeaderRow>

        <StyledProjectInfoRow>
          {isUserPermission ? (
            <>
              <StyledProjectInfoItem>
                <StyledInfoLabel>Username</StyledInfoLabel>
                <StyledInfoValue>{permissionData?.username || '-'}</StyledInfoValue>
              </StyledProjectInfoItem>
              <StyledProjectInfoItem>
                <StyledInfoLabel>User Email</StyledInfoLabel>
                <StyledInfoValue>{permissionData?.userEmail || '-'}</StyledInfoValue>
              </StyledProjectInfoItem>
              <StyledProjectInfoItem>
                <StyledInfoLabel>User Role</StyledInfoLabel>
                <StyledInfoValue>{permissionData?.userGroup || '-'}</StyledInfoValue>
              </StyledProjectInfoItem>
            </>
          ) : (
            <>
              <StyledProjectInfoItem>
                <StyledInfoLabel>Project ID</StyledInfoLabel>
                <StyledInfoValue>{permissions.GROUP_ID}</StyledInfoValue>
              </StyledProjectInfoItem>
              <StyledProjectInfoItem>
                <StyledInfoLabel>Project Name</StyledInfoLabel>
                <StyledInfoValue>{permissions.GROUP_NAME || '-'}</StyledInfoValue>
              </StyledProjectInfoItem>
              <StyledProjectInfoItem>
                <StyledInfoLabel>Client ID</StyledInfoLabel>
                <StyledInfoValue>{permissions.CLIENT_ID}</StyledInfoValue>
              </StyledProjectInfoItem>
              <StyledProjectInfoItem>
                <StyledInfoLabel>Client Name</StyledInfoLabel>
                <StyledInfoValue>{permissionData?.clientName || '-'}</StyledInfoValue>
              </StyledProjectInfoItem>
            </>
          )}
        </StyledProjectInfoRow>

        <StyledSectionTitle>Permissions</StyledSectionTitle>
        <StyledDivider />

        <StyledPermissionSection>
          <StyledSubSectionTitle>Jurisdictions</StyledSubSectionTitle>
          {renderChips(jurisdictions, 'JURISDICTION')}
        </StyledPermissionSection>

        <StyledPermissionSection>
          <StyledSubSectionTitle>Data Granularity</StyledSubSectionTitle>
          {renderChips(dataAggregations, 'DATA_AGGREGATION')}
        </StyledPermissionSection>

        {renderCategorizedItems(
          getFiltersByCategory(permissions.FILTERS, filterConfig),
          FILTER_CATEGORY_ORDER,
          'FILTERS',
          StyledCategoryTitle,
          'No filters available'
        )}

        <StyledSubSectionTitle>Use Cases Available</StyledSubSectionTitle>
        {renderCategorizedItems(
          getUseCasesByCategory(permissions.USECASE, useCases),
          USE_CASE_CATEGORY_ORDER,
          'USECASE',
          StyledSubSectionTitle,
          'No use cases available'
        )}

        <StyledPermissionSection>
          <StyledSubSectionTitle>Points of Interests</StyledSubSectionTitle>
          {renderChips(pointOfInterests, 'POINT_OF_INTEREST')}
        </StyledPermissionSection>

        <StyledPermissionSection>
          <StyledSubSectionTitle>Benefit Programs</StyledSubSectionTitle>
          {(() => {
            const hasBenefits = getFlagValue(permissions.BENEFITS)
            const benefitsDeletions = changesMap.BENEFITS?.deletions || []
            const benefitsInsertions = changesMap.BENEFITS?.insertions || []
            const hasBenefitChanges = showChanges && (benefitsDeletions.length > 0 || benefitsInsertions.length > 0)

            if (!hasBenefits && !hasBenefitChanges) {
              return <StyledNoDataText>No benefit programs available</StyledNoDataText>
            }

            return (
              <StyledChipContainer>
                {hasBenefits &&
                  (showChanges && benefitsInsertions.includes(1) ? (
                    <StyledInsertionChip label='+ SNAP' size='small' />
                  ) : (
                    <StyledChip label='SNAP' size='small' />
                  ))}
                {showChanges && benefitsDeletions.includes(1) && <StyledDeletionChip label='- SNAP' size='small' />}
              </StyledChipContainer>
            )
          })()}
        </StyledPermissionSection>

        {FLAG_PERMISSION_SECTIONS.map(({title, field}) => (
          <StyledPermissionSection key={field}>
            <StyledSubSectionTitle>{title}</StyledSubSectionTitle>
            {renderFlagChip(title, getFlagValue(permissions[field]), field)}
          </StyledPermissionSection>
        ))}

        <StyledPermissionSection>
          <StyledSubSectionTitle>HealthPrism Agent Export</StyledSubSectionTitle>
          {renderFlagChip(
            'HealthPrism Agent Export',
            getFlagValue(permissions.CHATBOT_EXPORT_FLAG),
            'CHATBOT_EXPORT_FLAG'
          )}
        </StyledPermissionSection>

        <StyledPermissionSection>
          <StyledSubSectionTitle>HealthPrism Agent Copy</StyledSubSectionTitle>
          {renderFlagChip('HealthPrism Agent Copy', getFlagValue(permissions.CHATBOT_COPY_FLAG), 'CHATBOT_COPY_FLAG')}
        </StyledPermissionSection>
      </>
    )
  }

  return (
    <StyledDialog open={open} onClose={onClose} aria-labelledby='view-permission-dialog-title' maxWidth='md'>
      <StyledDialogTitle id='view-permission-dialog-title'>
        <StyledTitleText>{title}</StyledTitleText>
        <StyledCloseButton aria-label='close' onClick={onClose} size='small'>
          <CloseIcon />
        </StyledCloseButton>
      </StyledDialogTitle>
      <StyledDialogContent dividers>{renderContent()}</StyledDialogContent>
    </StyledDialog>
  )
}

ViewPermissionModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  permissionData: PropTypes.object,
  title: PropTypes.string
}

ViewPermissionModal.defaultProps = {
  permissionData: null
}

export default ViewPermissionModal
